import { useEffect, useRef } from "react";
import { getWsUrl } from "@/lib/api";

/**
 * Subscribe to a specific auction's websocket room.
 *
 * Features:
 *   - Auto-reconnect with a short back-off after any close/error.
 *   - Heartbeat ping every 25 s so K8s ingress / Cloudflare don't drop the
 *     socket as idle after ~60 s. The backend answers `pong`.
 *   - Stale-connection detector: if we haven't received any frame (including
 *     the `pong` response to our own heartbeat) in 60 s the socket is treated
 *     as dead and force-reconnected. Fixes overlays that got "late" after
 *     ~25-30 events on production where the ingress silently half-closed the
 *     TCP stream so `onclose` never fired.
 *   - Fires a synthetic `{ type: "__reconnected" }` message on each successful
 *     (re)open so consumers can trigger a full HTTP reload of their data —
 *     this catches up on any missed broadcasts.
 *   - Strict-mode-safe close: if React's simulated unmount runs cleanup while
 *     the WebSocket is still in CONNECTING state, we defer the close until it
 *     actually opens (then close cleanly).
 *
 * onMessage(msg) is called for each parsed message (including the synthetic
 * reconnect signal).
 */
export function useAuctionSocket(auctionId, onMessage) {
  const wsRef = useRef(null);
  const handlerRef = useRef(onMessage);

  useEffect(() => { handlerRef.current = onMessage; }, [onMessage]);

  useEffect(() => {
    if (!auctionId) return;
    let cancelled = false;
    let reconnectTimer = null;
    let heartbeatTimer = null;
    let staleTimer = null;
    let lastMsgAt = Date.now();

    function scheduleHeartbeat(ws) {
      if (heartbeatTimer) clearInterval(heartbeatTimer);
      heartbeatTimer = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          try { ws.send("ping"); } catch (e) { /* noop */ }
        }
      }, 25000);
    }

    function scheduleStaleCheck(ws) {
      if (staleTimer) clearInterval(staleTimer);
      // Every 15 s, if we haven't received *any* frame in the last 60 s the
      // socket is half-dead → close it so onclose triggers our reconnect loop.
      staleTimer = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN && Date.now() - lastMsgAt > 60000) {
          try { ws.close(); } catch (e) { /* noop */ }
        }
      }, 15000);
    }

    function safeClose(ws) {
      if (!ws) return;
      if (ws.readyState === WebSocket.CONNECTING) {
        ws.addEventListener("open", () => { try { ws.close(); } catch (e) { /* noop */ } });
      } else if (ws.readyState === WebSocket.OPEN) {
        try { ws.close(); } catch (e) { /* noop */ }
      }
    }

    function connect() {
      if (cancelled) return;
      const ws = new WebSocket(getWsUrl(auctionId));
      wsRef.current = ws;
      lastMsgAt = Date.now();
      ws.onopen = () => {
        if (cancelled) { try { ws.close(); } catch (e) { /* noop */ } return; }
        lastMsgAt = Date.now();
        scheduleHeartbeat(ws);
        scheduleStaleCheck(ws);
        if (handlerRef.current) {
          try { handlerRef.current({ type: "__reconnected" }); } catch (e) { /* noop */ }
        }
      };
      ws.onmessage = (ev) => {
        lastMsgAt = Date.now();
        if (ev.data === "pong") return;
        try {
          const data = JSON.parse(ev.data);
          if (handlerRef.current) handlerRef.current(data);
        } catch (e) { /* ignore */ }
      };
      ws.onclose = () => {
        if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null; }
        if (staleTimer) { clearInterval(staleTimer); staleTimer = null; }
        if (cancelled) return;
        reconnectTimer = setTimeout(connect, 2000);
      };
      ws.onerror = () => { safeClose(ws); };
    }

    // Force a fresh handshake when the tab becomes visible again — mobile
    // browsers suspend inactive WS which then look "connected" but never
    // deliver frames on wake.
    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      if (Date.now() - lastMsgAt > 30000) {
        try { ws.close(); } catch (e) { /* noop */ }
      }
    };
    document.addEventListener("visibilitychange", onVis);

    connect();
    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (heartbeatTimer) clearInterval(heartbeatTimer);
      if (staleTimer) clearInterval(staleTimer);
      document.removeEventListener("visibilitychange", onVis);
      safeClose(wsRef.current);
    };
  }, [auctionId]);
}
